import { DefaultTheme, ExecutionContext, styled } from 'styled-components';
import { ThemeProps } from '../../themes';
import { InformationType, Size, Sizes } from '../../types';
import { getColorByInfoType, InformationMessageProps } from '../input.styles';

/** Chevron icon size (px). Exported so dropdown.tsx renders the icon at the
 *  same size the styles reserve space for. */
export const CHEVRON_SIZE = 16;
/** Gap between chevron and adjacent text. */
const CHEVRON_GAP = 8;
/** Space inside the content area that the chevron occupies (size + gap).
 *  Used by SelectDisplay (already inside the container's horizontal padding). */
const chevronInnerReserve = `${CHEVRON_SIZE + CHEVRON_GAP}px`;
/** Right-edge offset of the chevron — matches the container's horizontal
 *  padding so the chevron aligns with the content's left edge mirror-image. */
const chevronRight = (theme: DefaultTheme, size: Sizes = Size.NORMAL) =>
    theme.componentSizes.inputField[size].horizontalPadding;
/** Total gutter from the container's right edge that absolutely-positioned
 *  elements (Label) need to clear the chevron. */
const chevronOuterGutter = (theme: DefaultTheme, size: Sizes = Size.NORMAL) =>
    `${chevronRight(theme, size) + CHEVRON_SIZE + CHEVRON_GAP}px`;

interface ContainerProps {
    $hasLabel: boolean;
    $disabled?: boolean;
    $interactionsDisabled?: boolean;
    $size?: Size;
}

function getContainerBorderColor({
    theme,
    $hasMessage,
    $informationType,
    $invalid,
}: InformationMessageProps & ExecutionContext) {
    if ($invalid) {
        return `${theme.inputMessageBorder} ${theme.inputMessageColorError}`;
    }
    if ($hasMessage) {
        return `${theme.inputMessageBorder} ${getColorByInfoType(theme, $informationType)}`;
    }
    return theme.inputBorder;
}

function getContainerBorderColorFocused({
    theme,
    $hasMessage,
    $informationType,
    $invalid,
}: InformationMessageProps & ExecutionContext) {
    if ($invalid || ($hasMessage && $informationType === InformationType.ERROR)) {
        return `${theme.inputMessageBorder} ${theme.inputMessageColorError}`;
    }
    return theme.inputBorderActive;
}

export const Container = styled.div<ContainerProps & InformationMessageProps>`
    display: flex;
    flex-direction: column;
    /* When a label is present, pin the SelectDisplay to the bottom so the
       absolutely-positioned Label has clear space at the top when it's in
       its active (small-at-top) state. Otherwise just center the value.
       Same pattern as TextInput. */
    justify-content: ${({ $hasLabel }) => ($hasLabel ? 'flex-end' : 'center')};
    align-items: flex-start;
    border-radius: ${({ theme, $size = Size.NORMAL }) =>
        theme.componentSizes.inputField[$size].radius}px;
    padding: ${({ theme, $size = Size.NORMAL }) => {
        const s = theme.componentSizes.inputField[$size];
        return `${s.verticalPadding}px ${s.horizontalPadding}px`;
    }};
    position: relative;
    box-sizing: border-box;

    opacity: ${({ $disabled, $interactionsDisabled }) =>
        $disabled && !$interactionsDisabled ? '0.5' : '1'};
    cursor: ${({ $disabled, $interactionsDisabled }) =>
        $disabled && !$interactionsDisabled ? 'not-allowed' : 'inherit'};
    pointer-events: ${({ $interactionsDisabled }) => ($interactionsDisabled ? 'none' : 'inherit')};
    background: ${({ theme }) => theme.inputBackground};
    height: ${({ theme, $size = Size.NORMAL }) => theme.componentSizes.inputField[$size].height}px;

    &:after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        border-radius: ${({ theme }) => theme.spacing(3)};
        border: ${getContainerBorderColor};
        pointer-events: none;
    }

    &:focus-within {
        background: ${({ theme }) => theme.inputActiveBackground};
        &:after {
            border: ${getContainerBorderColorFocused};
        }
    }
`;

export const Select = styled.select<{ $interactionsDisabled: boolean; $size?: Size }>`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1;
    border: none;
    text-indent: -100vw; /* hide native text */
    padding: 0;
    outline: none;
    background-color: transparent !important;
    pointer-events: ${({ $interactionsDisabled }) => ($interactionsDisabled ? 'none' : 'inherit')};
    cursor: ${({ disabled, $interactionsDisabled }) =>
        disabled && !$interactionsDisabled ? 'not-allowed' : 'inherit'};
    appearance: none;
    /* Sets the font-size of the native OS-rendered option list. Fixed across
       all Sizes to match SelectDisplay (the visible value). */
    font-size: ${({ theme }) => theme.typography.fontSize.description}px;
    height: 100%;

    /* iOS zooms the page into any field under 16px when it gets focus. The select's own text is
       hidden (SelectDisplay shows the value), so on a touch screen it can be 16px at no cost. */
    @media (hover: none) and (pointer: coarse) {
        font-size: max(16px, ${({ theme }) => theme.typography.fontSize.description}px);
    }

    /* Gray out the placeholder */
    option:first-child {
        color: ${({ theme }) => theme.colorTextDisabled};
    }
`;

type SelectDisplayProps = {
    $noWrap?: boolean;
    $size?: Size;
    $bold?: boolean;
    $hasValue: boolean;
    $hasLabel?: boolean;
};

export const SelectDisplay = styled.div<SelectDisplayProps>`
    color: ${({ theme, $hasValue }) =>
        $hasValue ? theme.inputInputColor || '#000' : theme.inputLabelColor};
    font-family: ${({ theme }) => theme.typography.secondaryFontFamily};
    font-size: ${({ theme }) => theme.typography.fontSize.description}px;
    font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
    line-height: 1.25;
    /* Reserve right-side space so values don't render underneath the
       absolutely-positioned chevron in IconWrapper. */
    padding: 0 ${chevronInnerReserve} 0 0;
    width: 100%;
    box-sizing: border-box;
    ${({ $noWrap }) =>
        $noWrap &&
        `
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `}
`;

export const Label = styled.label<{ $size?: Size; $elevated?: boolean }>`
    display: block;
    position: absolute;
    z-index: 1;
    /* Left-aligned with the SelectDisplay's content edge. */
    left: ${({ theme, $size = Size.NORMAL }) =>
        theme.componentSizes.inputField[$size].horizontalPadding}px;
    /* Clear the chevron. */
    right: ${({ theme, $size }) => chevronOuterGutter(theme, $size)};
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.inputLabelColor};
    font-family: ${({ theme }) => theme.typography.secondaryFontFamily};
    font-weight: 400;
    pointer-events: none;
    transition: all 0.2s ease-in-out;

    /* Inactive (empty + unfocused): vertically centered, larger — acts as placeholder. */
    top: 50%;
    transform: translateY(-50%);
    font-size: ${({ theme }) => theme.typography.fontSize.description}px;
    line-height: normal;

    /* Active (focused or has a value): small at the top of the container. */
    ${({ $elevated, theme, $size = Size.NORMAL }) => {
        const elevatedStyles = `
            top: ${theme.componentSizes.inputField[$size].verticalPadding}px;
            transform: translateY(0);
            font-size: ${theme.typography.fontSize.descriptionSmall}px;
            line-height: 1.3;
        `;
        return $elevated ? elevatedStyles : `${Select}:focus ~ & { ${elevatedStyles} }`;
    }}
`;

/**
 * Invisible, in-flow span that mirrors the elevated Label's text and font.
 * The real Label is `position: absolute` and so doesn't contribute to the
 * Container's intrinsic width — without this spacer, a short value like "25"
 * could let the container shrink narrower than the label "Rows", clipping
 * the label. The spacer reserves enough width for the elevated label.
 */
export const LabelWidthReserver = styled.span`
    visibility: hidden;
    height: 0;
    overflow: hidden;
    white-space: nowrap;
    pointer-events: none;
    font-family: ${({ theme }) => theme.typography.secondaryFontFamily};
    /* Use the LARGER (inactive) font-size so the reservation covers both the
       inactive (description) and elevated (descriptionSmall) label states. */
    font-size: ${({ theme }) => theme.typography.fontSize.description}px;
    /* Match the label's right-side gutter so it accounts for the chevron
       when reserving width. */
    padding-right: ${chevronInnerReserve};
`;

export const IconWrapper = styled.div<ThemeProps & { $size?: Size }>`
    position: absolute;
    top: 0;
    right: ${({ theme, $size }) => chevronRight(theme, $size)}px;
    height: 100%;
    display: flex;
    align-items: center;
    pointer-events: none;
    z-index: 2;
`;

export const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;
